import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { IonCol, IonIcon, IonItemDivider, IonLabel, IonRow } from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  InfiniteScrollHeaderComponent,
  type IInfiniteScrollHeaderConfig,
  type IInfiniteScrollHeaderFilter,
} from '@okcargo-apps/layout-ionic';
import { FreightAvailableListSectionComponent } from '../freight-available-list-section/freight-available-list-section.component';
import { FreightAvailableStoreService } from '../freight-available-store.service';

@Component({
  selector: 'lib-freight-available-list',
  templateUrl: './freight-available-list.component.html',
  styleUrls: ['./freight-available-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    InfiniteScrollHeaderComponent,
    FreightAvailableListSectionComponent,
    IonItemDivider,
    IonRow,
    IonCol,
    IonIcon,
    IonLabel,
  ],
})
export class FreightAvailableListComponent {
  private readonly freightAvailableStoreService = inject(FreightAvailableStoreService);
  private readonly translateService = inject(TranslateService);

  protected readonly createdList = this.freightAvailableStoreService.freightAvailableList;
  protected readonly notCreatedFilteredList = this.freightAvailableStoreService.freightAvailableAdditionalList;
  protected readonly hasActiveFilters = this.freightAvailableStoreService.hasActiveFilters;
  protected readonly createdFilteredExhausted = this.freightAvailableStoreService.freightAvailableFilteredExhausted;

  protected readonly headerConfig = computed<IInfiniteScrollHeaderConfig>(() => {
    return {
      text: this.translateService.instant('market.item_count', {
        count: this.freightAvailableStoreService.freightsAvailableFilteredTotalElements(),
      }),
      lastUpdated: this.createdList().lastLoadedAt,
      staleMessage: this.createdList().isStale ? this.translateService.instant('general.stale_data') : undefined,
      refreshButton: this.createdList().isStale
        ? {
            label: this.translateService.instant('general.refresh'),
            action: () => this.onRefreshUser(),
          }
        : undefined,
      appliedFilters: this.appliedFiltersChips(),
    };
  });

  private appliedFiltersChips = computed<IInfiniteScrollHeaderFilter[]>(() => {
    const filters = this.freightAvailableStoreService.activeFilters();
    if (!filters) return [];

    const chips: IInfiniteScrollHeaderFilter[] = [];

    const origin = (filters.originCountryCode ?? '') + (filters.originStateCode ?? '');
    const destination = (filters.destinationCountryCode ?? '') + (filters.destinationStateCode ?? '');

    if (origin) {
      chips.push({
        label: `${this.translateService.instant('general.origin')}: ${origin}`,
        clear: () => this.freightAvailableStoreService.clearOriginFilter(),
      });
    }
    if (destination) {
      chips.push({
        label: `${this.translateService.instant('general.destination')}: ${destination}`,
        clear: () => this.freightAvailableStoreService.clearDestinationFilter(),
      });
    }

    return chips;
  });

  protected onCreatedLoadNext(): void {
    this.freightAvailableStoreService.loadFreightAvailablePage();
  }

  protected onCreatedRetry(): void {
    this.freightAvailableStoreService.retryFreightAvailable();
  }

  protected onNotCreatedLoadNext(): void {
    this.freightAvailableStoreService.loadNextFreightAvailableAdditionalPage();
  }

  protected onNotCreatedRetry(): void {
    this.freightAvailableStoreService.retryFreightAvailableAdditional();
  }

  protected onRefreshUser(): void {
    this.freightAvailableStoreService.reloadFreightAvailableList('user');
  }
}
